import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  formatAmountInput,
  formatEuro,
  formatInteger,
  formatPercent,
  formatSquareMeters,
  parseAmountInput,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Combobox,
  FilterChip,
  SearchField,
  Segmented,
  Select,
  Skeleton,
  TextField,
  type ComboboxOption,
} from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { errorMessage } from '../../../lib/api';
import { useAllUnits, useContactSearch, useProperties } from '../api';
import { availabilityPill } from '../availability';
import { StatusPill } from '../bits';
import type { AllUnitRow, AllUnitsResponse, UnitFilter } from '../types';
import {
  ALL_UNITS_SORT_OPTIONS,
  activeFilterCount,
  budgetChipLabel,
  calculatorHref,
  filterFromParams,
  readNumber,
} from './allUnitsFilter';
import { ListSwitch } from './ListSwitch';
import styles from './AllUnitsPage.module.css';

type Tab = Exclude<UnitFilter, 'unavailable'>;

function countOf(counts: AllUnitsResponse['counts'], tab: Tab): number {
  if (tab === 'all') return counts.all;
  if (tab === 'not_released') return counts.notReleased;
  return counts[tab];
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Euro amount of the URL („250000") as the field's editing text („250.000,00"). */
function amountText(value: string | null): string {
  const euros = readNumber(value);
  return euros === undefined ? '' : formatAmountInput(Math.round(euros * 100));
}

/** The field's editing text → the URL value in euros; null when empty or unreadable. */
function amountParam(text: string): string | null {
  const cents = parseAmountInput(text);
  return cents === null || cents < 0 ? null : String(cents / 100);
}

function areaParam(text: string): string | null {
  const value = readNumber(text.trim());
  return value === undefined ? null : String(value);
}

/** The draft fields of the filter card: typed text that reaches the URL 350 ms after the last keystroke. */
const DRAFT_KEYS = ['q', 'priceMin', 'priceMax', 'areaMin', 'areaMax', 'budget'] as const;
type DraftKey = (typeof DRAFT_KEYS)[number];

function draftsFromParams(params: URLSearchParams): Record<DraftKey, string> {
  return {
    q: params.get('q') ?? '',
    priceMin: amountText(params.get('priceMin')),
    priceMax: amountText(params.get('priceMax')),
    areaMin: params.get('areaMin') ?? '',
    areaMax: params.get('areaMax') ?? '',
    budget: amountText(params.get('budget')),
  };
}

function draftParam(key: DraftKey, text: string): string | null {
  if (key === 'q') return text.trim() === '' ? null : text.trim();
  if (key === 'areaMin' || key === 'areaMax') return areaParam(text);
  return amountParam(text);
}

function UnitCell({ row }: { row: AllUnitRow }) {
  return (
    <span role="cell" className={styles.unitCell}>
      <span className={styles.number}>{row.number}</span>
      <span className={styles.sub}>{row.typeLabel}</span>
    </span>
  );
}

function AvailabilityCell({ row }: { row: AllUnitRow }) {
  const pill = availabilityPill(row.availability);
  return (
    <span role="cell" className={styles.availability}>
      <StatusPill tone={pill.tone} unit>
        {row.availabilityLabel}
      </StatusPill>
    </span>
  );
}

function money(cents: number | null): string {
  return cents === null ? '—' : formatEuro(cents);
}

function UnitRowView({
  row,
  onOpen,
  onCalculator,
}: {
  row: AllUnitRow;
  onOpen: () => void;
  onCalculator: () => void;
}) {
  return (
    <div
      role="row"
      tabIndex={0}
      className={styles.row}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <UnitCell row={row} />
      <span role="cell" className={styles.propertyCell}>
        <span className={styles.propertyName}>{row.property.name}</span>
        <span className={styles.sub}>{row.property.city ?? '—'}</span>
      </span>
      <span role="cell" className={clsx(styles.numeric, styles.area)}>
        {row.areaSqm === null ? '—' : formatSquareMeters(row.areaSqm)}
      </span>
      <span role="cell" className={clsx(styles.numericStack, styles.price)}>
        <span className={styles.numeric}>{money(row.priceCents)}</span>
        <span className={styles.sub}>
          {row.pricePerSqmCents === null ? '' : `${formatEuro(row.pricePerSqmCents)}/m²`}
        </span>
      </span>
      <span role="cell" className={clsx(styles.numeric, styles.total)}>
        <span className={styles.mobileLabel}>Gesamtinvestition</span>
        {money(row.totalInvestmentCents)}
      </span>
      <span role="cell" className={clsx(styles.numeric, styles.rent)}>
        <span className={styles.mobileLabel}>Soll-Miete</span>
        {money(row.rentTargetCents)}
      </span>
      <span role="cell" className={clsx(styles.numeric, styles.yield)}>
        <span className={styles.mobileLabel}>Rendite</span>
        {row.grossYieldPct === null ? '—' : formatPercent(row.grossYieldPct, 2)}
      </span>
      <AvailabilityCell row={row} />
      <span role="cell" className={styles.actions} onClick={(event) => event.stopPropagation()}>
        <button type="button" className={clsx('pressable', styles.rowButton)} onClick={onCalculator}>
          Rechner starten
        </button>
      </span>
    </div>
  );
}

function RowSkeleton() {
  return (
    <div className={styles.row} aria-hidden="true">
      <Skeleton width={64} height={28} />
      <Skeleton width="70%" height={28} />
      <Skeleton width={50} height={14} />
      <Skeleton width={80} height={28} />
      <Skeleton width={80} height={14} />
      <Skeleton width={60} height={14} />
      <Skeleton width={44} height={14} />
      <Skeleton width={70} height={22} />
      <span />
    </div>
  );
}

/** „Alle Einheiten" (owner decision 19.10.2026, D-270): `/properties/units?q=&propertyId=&availability=&…`. */
export function AllUnitsPage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => filterFromParams(params), [params]);

  const [drafts, setDrafts] = useState(() => draftsFromParams(params));
  const debounced = useDebounced(drafts, 350);
  useEffect(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        let changed = false;
        for (const key of DRAFT_KEYS) {
          const value = draftParam(key, debounced[key]);
          if ((current.get(key) ?? null) === value) continue;
          changed = true;
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return changed ? next : current;
      },
      { replace: true },
    );
  }, [debounced, setParams]);

  function patch(changes: Record<string, string | null>) {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  }

  function reset() {
    setDrafts({ q: '', priceMin: '', priceMax: '', areaMin: '', areaMax: '', budget: '' });
    setParams(new URLSearchParams(), { replace: true });
  }

  const list = useAllUnits(filter);
  const first = list.data?.pages[0];
  const items = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data]);

  // „Objekt": every property the user can see, loaded page by page through the overview's hook
  const properties = useProperties({});
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = properties;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const propertyOptions = useMemo(
    () => [
      { value: '', label: 'Alle Objekte' },
      ...(properties.data?.pages.flatMap((page) => page.items) ?? []).map((item) => ({
        value: item.id,
        label: item.city ? `${item.name} · ${item.city}` : item.name,
      })),
    ],
    [properties.data],
  );

  // „Budget des Mandanten": contact search, the chosen contact comes back with its budget
  const [contactQuery, setContactQuery] = useState('');
  const search = useContactSearch(contactQuery, contactQuery.trim().length >= 2);
  const contactOptions = useMemo<ComboboxOption[]>(() => {
    const options: ComboboxOption[] = (search.data?.items ?? []).map((hit) => ({
      id: hit.id,
      label: `${hit.firstName} ${hit.lastName}`.trim(),
      description: hit.deal ?? undefined,
    }));
    const chosen = first?.contact;
    if (chosen && !options.some((option) => option.id === chosen.id)) {
      options.unshift({ id: chosen.id, label: chosen.name });
    }
    return options;
  }, [search.data, first?.contact]);

  const counts = first?.counts;
  const tab: Tab = filter.availability === 'unavailable' ? 'all' : (filter.availability ?? 'all');
  const tabItems: { value: Tab; label: string }[] = [
    { value: 'all', label: 'Alle' },
    { value: 'available', label: 'Frei' },
    { value: 'reserved', label: 'Reserviert' },
  ];
  if (counts && counts.exclusive > 0) tabItems.push({ value: 'exclusive', label: 'Exklusiv' });
  tabItems.push({ value: 'sold', label: 'Verkauft' });
  if (counts && counts.notReleased > 0) tabItems.push({ value: 'not_released', label: 'Nicht freigegeben' });
  const tabs = tabItems.map((item) => ({
    ...item,
    label: counts ? `${item.label} · ${formatInteger(countOf(counts, item.value))}` : item.label,
  }));

  const filters = activeFilterCount(filter);
  const contactChosen = filter.contactId !== undefined && first?.contact?.id === filter.contactId;
  const setDraft = (key: DraftKey) => (value: string) =>
    setDrafts((current) => ({ ...current, [key]: value }));

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1 className={styles.title}>Alle Einheiten</h1>
        <span className={styles.summary}>{first ? first.summary : <Skeleton width={120} height={12} />}</span>
        <ListSwitch value="units" />
      </div>

      <section className={styles.filters} aria-label="Filter">
        <div className={styles.filterGrid}>
          <SearchField
            label="Suche"
            labelVisible
            className={styles.searchField}
            placeholder="Einheit, Objekt, Ort oder Straße"
            value={drafts.q}
            onChange={(event) => setDraft('q')(event.target.value)}
          />
          <Select
            label="Objekt"
            className={styles.propertyField}
            value={filter.propertyId ?? ''}
            options={propertyOptions}
            onChange={(event) => patch({ propertyId: event.target.value || null })}
          />
          <div className={styles.range}>
            <TextField
              label="Kaufpreis von"
              type="money"
              value={drafts.priceMin}
              onChange={(event) => setDraft('priceMin')(event.target.value)}
            />
            <TextField
              label="Kaufpreis bis"
              type="money"
              value={drafts.priceMax}
              onChange={(event) => setDraft('priceMax')(event.target.value)}
            />
          </div>
          <div className={styles.range}>
            <TextField
              label="Wohnfläche von"
              inputMode="decimal"
              trailing={<span className={styles.unitSuffix}>m²</span>}
              value={drafts.areaMin}
              onChange={(event) => setDraft('areaMin')(event.target.value)}
            />
            <TextField
              label="Wohnfläche bis"
              inputMode="decimal"
              trailing={<span className={styles.unitSuffix}>m²</span>}
              value={drafts.areaMax}
              onChange={(event) => setDraft('areaMax')(event.target.value)}
            />
          </div>
          <div className={styles.budget}>
            <Combobox
              label="Budget des Mandanten"
              className={styles.contactField}
              options={contactOptions}
              value={filter.contactId ?? null}
              onChange={(id) => patch({ contactId: id })}
              onQueryChange={setContactQuery}
              filter={false}
              placeholder="Mandant suchen"
              emptyText={contactQuery.trim().length < 2 ? 'Mindestens zwei Zeichen eingeben' : 'Kein Treffer'}
            />
            <TextField
              label="Budget"
              type="money"
              className={styles.budgetField}
              value={drafts.budget}
              onChange={(event) => setDraft('budget')(event.target.value)}
            />
          </div>
        </div>
        {contactChosen || filters > 0 ? (
          <div className={styles.chips}>
            {contactChosen && first?.contact ? (
              <FilterChip
                label={`${first.contact.name} · ${budgetChipLabel(first.budget)}`}
                active={first.budget !== null}
                onClick={() => patch({ contactId: null })}
                onRemove={() => patch({ contactId: null })}
              />
            ) : null}
            {filters > 0 ? (
              <Button variant="ghost" size="small" onClick={reset}>
                Filter zurücksetzen
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className={styles.card} aria-label="Einheiten">
        <div className={styles.cardHead}>
          <Segmented<Tab>
            label="Verfügbarkeit"
            className={styles.tabs}
            value={tab}
            items={tabs}
            onChange={(next) => patch({ availability: next === 'all' ? null : next })}
          />
          {!mobile ? (
            <div className={styles.sort}>
              <span>Sortiert nach</span>
              <Select
                label="Sortierung"
                labelHidden
                size="compact"
                className={styles.sortSelect}
                value={filter.sort ?? 'property'}
                options={ALL_UNITS_SORT_OPTIONS}
                onChange={(event) =>
                  patch({ sort: event.target.value === 'property' ? null : event.target.value })
                }
              />
            </div>
          ) : null}
        </div>

        {list.isError ? (
          <div className={styles.state}>
            <Banner
              tone="danger"
              actions={
                <Button variant="secondary" size="small" onClick={() => void list.refetch()}>
                  Erneut laden
                </Button>
              }
            >
              {errorMessage(list.error)}
            </Banner>
          </div>
        ) : (
          <>
            {!mobile ? (
              <div className={styles.tableHead} role="row">
                <span role="columnheader">Einheit</span>
                <span role="columnheader">Objekt</span>
                <span role="columnheader" className={styles.end}>
                  Wohnfläche
                </span>
                <span role="columnheader" className={styles.end}>
                  Kaufpreis
                </span>
                <span role="columnheader" className={styles.end}>
                  Gesamtinvestition
                </span>
                <span role="columnheader" className={styles.end}>
                  Soll-Miete
                </span>
                <span role="columnheader" className={styles.end}>
                  Rendite
                </span>
                <span role="columnheader">Verfügbarkeit</span>
                <span role="columnheader" aria-label="Aktionen" />
              </div>
            ) : null}
            <div role="table" aria-label="Alle Einheiten" aria-busy={list.isPending || undefined}>
              {list.isPending
                ? Array.from({ length: 6 }, (_, index) => <RowSkeleton key={index} />)
                : items.map((row) => (
                    <UnitRowView
                      key={row.id}
                      row={row}
                      onOpen={() => void navigate(`/properties/${row.propertyId}/units/${row.id}`)}
                      onCalculator={() =>
                        void navigate(calculatorHref(row.propertyId, row.id, filter.contactId))
                      }
                    />
                  ))}
              {!list.isPending && items.length === 0 ? (
                <div className={styles.empty}>Keine Einheiten gefunden.</div>
              ) : null}
            </div>
            <div className={styles.foot}>
              <span className={styles.footText}>
                {first
                  ? `${formatInteger(items.length)} von ${formatInteger(countOf(first.counts, tab))} Einheiten`
                  : ''}
              </span>
              {list.hasNextPage ? (
                <Button
                  variant="secondary"
                  size="small"
                  className={styles.more}
                  loading={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  Mehr laden
                </Button>
              ) : null}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
